import React, {
  ImgHTMLAttributes,
  ReactNode,
  useEffect,
  useState,
} from "react";
import cn from "classnames";
import ImageOutlined from "@mui/icons-material/ImageOutlined";
import styles from "./SafeImage.module.scss";

export type SafeImageProps = Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "src" | "alt" | "onError"
> & {
  src?: string | null;
  /** Describe the image, or "" when it is decorative. */
  alt: string;
  /** Placeholder image when `src` is missing or fails to load… */
  fallbackSrc?: string;
  /** …or any node, e.g. an icon or initials. */
  fallback?: ReactNode;
};

/**
 * An <img> that never renders with an empty src (which refetches the page)
 * and swaps in a fallback when the file fails to load, e.g. an expired S3
 * link, instead of the browser's broken-image glyph.
 */
const SafeImage = ({
  src,
  alt,
  fallbackSrc,
  fallback,
  className,
  width,
  height,
  ...props
}: SafeImageProps) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);

  const image = (url: string, onError?: () => void) => (
    <img
      {...props}
      src={url}
      alt={alt}
      width={width}
      height={height}
      className={className}
      onError={onError}
    />
  );

  if (src && !failed) return image(src, () => setFailed(true));
  if (fallbackSrc) return image(fallbackSrc);
  if (fallback !== undefined) return <>{fallback}</>;
  return (
    <span
      className={cn(styles.fallback, className)}
      style={{ width, height }}
      role={alt ? "img" : undefined}
      aria-label={alt || undefined}
      aria-hidden={alt ? undefined : true}
    >
      <ImageOutlined fontSize="inherit" />
    </span>
  );
};

export default SafeImage;
